/* ==========================================================================
   AURUM — "GET AN OFFER" flow (contact-first).
   Opened by any [data-present] CTA. Mobile-first, under ~2 minutes.
   ONLY email is required. Photos and all context are optional and never block.

   Steps:
     1. Capture   email required (+ name optional + visible, optional context).
     2. Photos    strongly encouraged, never required ("Continue Without Photos").
     3. Confirm   email-first, no phone, no scheduling, no appraisal language.

   ONE email per lead, sent on the FINAL submit (which is one tap past the email
   step), so Butch never gets duplicate notifications.

   DELIVERY: routes through window.MBMForms.send (the shared FormSubmit router)
   when present, else a local FormSubmit POST. The FINAL submit is SUCCESS-GATED:
   the confirmation shows ONLY on a real HTTP 200; on failure an honest mailto
   fallback appears so the lead is never lost. Reply-To is the seller; an
   autoresponder confirms the lead. PHOTOS: when Cloudinary is configured
   (CLOUDINARY_CLOUD + CLOUDINARY_PRESET below), each selected photo uploads from
   the seller's device and the lead email carries clickable image links. While
   unconfigured, photos are listed by filename only and requested in Butch's reply
   (FormSubmit's JSON path cannot carry file uploads).
   ========================================================================== */
(function () {
  'use strict';

  var SUBJECT = 'New SELL lead (AURUM Get an Offer)';   // internal lead subject; ONE email per lead on final submit
  // Seller confirmation copy + the backend endpoint now live ONCE in the shared
  // MBMForms adapter (CONFIRMATIONS.acquisition + BACKEND config). This flow just
  // hands the lead to MBMForms.send with kind:'acquisition'.

  /* Cloudinary UNSIGNED upload. Fill these in after creating a free Cloudinary
     account + an UNSIGNED upload preset. While empty, photos are listed by
     filename only (no upload). When set, each selected photo uploads from the
     seller's device and the lead email carries clickable image links. */
  var CLOUDINARY_CLOUD = 'dojnv2mls';      // cloud name (public-safe; appears in every Cloudinary URL)
  var CLOUDINARY_PRESET = 'mbm_unsigned';  // UNSIGNED upload preset (folder: mbm-acquisitions)

  /* Cloudflare Turnstile (invisible spam check; same widget/site key as the offer accept form).
     Explicit render because the photos screen is hidden until reached. Non-blocking: if no token,
     the lead still submits (Basin's mailto fallback covers the rare case), so this never hard-breaks. */
  var TURNSTILE_SITEKEY = '0x4AAAAAADtEflh5kBgKCKOO';
  var tsWidgetId = null;
  /* PHASE 1 (2026-08-24) — TURNSTILE LIFECYCLE. The old render had no expiry
     handling: the token minted when the photos screen opened died after ~5
     minutes (a seller picking photos easily takes longer), the widget never
     refreshed, and the empty token submitted anyway. Basin form 71040 has
     force_turnstile ON, so those leads arrived spam-flagged as
     `failed_turnstile` with NO notification and NO autoresponder (proven:
     submissions 61216038/61216039 and the 7/25 pair). This is the deployed
     form of the incident package's PATCHED2 correction. tsState is telemetry
     only; delivery never blocks on it. */
  var tsState = 'unloaded';   /* unloaded|ready|ok|expired|error */
  function loadTurnstile() {
    if (window.turnstile || document.getElementById('cf-turnstile-api')) return;
    var s = document.createElement('script');
    s.id = 'cf-turnstile-api';
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true; s.defer = true;
    document.head.appendChild(s);
  }
  function renderTurnstile() {
    var box = flow && flow.querySelector('#pycTurnstile');
    if (!box || tsWidgetId !== null) return;          /* render once; persists with the flow */
    var tries = 0;
    (function attempt() {
      if (window.turnstile && typeof window.turnstile.render === 'function') {
        try {
          tsWidgetId = window.turnstile.render(box, {
            sitekey: TURNSTILE_SITEKEY,
            theme: 'dark',
            'refresh-expired': 'auto',
            callback: function () { tsState = 'ok'; },
            'expired-callback': function () { tsState = 'expired'; },
            'timeout-callback': function () { tsState = 'expired'; },
            'error-callback': function () { tsState = 'error'; return true; }
          });
          if (tsWidgetId !== null && tsState === 'unloaded') tsState = 'ready';
        } catch (e) { tsState = 'error'; }
      } else if (tries++ < 40) { setTimeout(attempt, 200); }
    })();
  }
  function turnstileToken() {
    try { return (window.turnstile && tsWidgetId !== null) ? (window.turnstile.getResponse(tsWidgetId) || '') : ''; }
    catch (e) { return ''; }
  }
  /* Submit-time token: if the current token is gone (expired while the seller
     picked photos), reset the widget and wait up to ~8s for a fresh one. On
     anything unexpected, resolve with whatever we have — NEVER block the lead. */
  function freshTurnstileToken() {
    return new Promise(function (resolve) {
      var t = turnstileToken();
      if (t) { resolve(t); return; }
      if (!window.turnstile || tsWidgetId === null) { resolve(''); return; }
      try { window.turnstile.reset(tsWidgetId); } catch (e) { resolve(''); return; }
      /* absolute deadline, not iteration counting: a backgrounded tab throttles
         chained timers to ~1/min, which would stretch 20 iterations to 20
         minutes. With a wall-clock deadline the first callback past 8s resolves. */
      var deadline = Date.now() + 8000;
      (function poll() {
        var tok = turnstileToken();
        if (tok) { tsState = 'ok'; resolve(tok); return; }
        if (Date.now() >= deadline) { resolve(''); return; }
        setTimeout(poll, 400);
      })();
    });
  }

  var STEP_OF = { capture: 1, photos: 2, done: 3 };

  var flow, els = {}, lastTrigger = null;
  var state = resetState();

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function resetState() {
    return { screen: 'capture', photos: [], photoUrls: [], photoFailed: 0, name: '', email: '', type: [], heard: '', payout: '', note: '', dontknow: false, submitted: false,
             leadRef: '', cta: '', sentPhotosEvent: false, sentPhotoAddedEvent: false, inFlight: false };
  }

  /* ===== PHASE 1 (2026-08-24): lead_ref + CTA + telemetry ================
     lead_ref is a RANDOM 12-hex id minted when the flow opens. It is not
     derived from the seller or the device, it is shown to the seller as
     "Reference MBM-XXXXXX" on the done screen, it rides in the Basin payload
     as "Lead ref", and the same value goes to GA4 — that is the whole join:
     GA4 event <-> Basin submission <-> (via basin_id) Vault deal. Never
     replace this with email or any seller identifier. One lead_ref per flow
     session: reopening an unsubmitted flow keeps it; a successful submit
     hard-resets, so the next flow mints a new one. */
  function mintLeadRef() {
    try {
      var a = new Uint8Array(6); crypto.getRandomValues(a);
      return Array.prototype.map.call(a, function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
    } catch (e) {
      /* no crypto: fall back to Math.random — still non-PII, still unique enough */
      var s = ''; while (s.length < 12) s += Math.floor(Math.random() * 16).toString(16);
      return s.slice(0, 12);
    }
  }
  function refCode() { return state.leadRef ? ('MBM-' + state.leadRef.slice(0, 6).toUpperCase()) : ''; }
  function track(name, params) {
    try { if (window.MBMForms && window.MBMForms.track) window.MBMForms.track(name, params); } catch (e) {}
  }

  /* ---------------------------------------------------------------- build */
  function build() {
    flow = document.createElement('div');
    flow.id = 'pycFlow';
    flow.setAttribute('role', 'dialog');
    flow.setAttribute('aria-modal', 'true');
    flow.setAttribute('aria-labelledby', 'pycTitleCapture');
    flow.setAttribute('aria-hidden', 'true');
    flow.innerHTML =
      '<div class="pyc-scrim" data-pyc-dismiss></div>' +
      '<div class="pyc-modal" role="document">' +
        '<div class="pyc-head">' +
          '<div class="pyc-progress">' +
            '<div class="pyc-steps" aria-hidden="true">' +
              '<span class="pyc-step" data-step="1"></span>' +
              '<span class="pyc-step" data-step="2"></span>' +
              '<span class="pyc-step" data-step="3"></span>' +
            '</div>' +
            '<span class="pyc-stepnum" id="pycStepNum">Step 1 of 3</span>' +
          '</div>' +
          '<button class="pyc-close" type="button" data-pyc-dismiss aria-label="Close">' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
          '</button>' +
        '</div>' +

        '<div class="pyc-body">' +

          /* SCREEN 1 — CAPTURE (only email required) */
          '<section class="pyc-screen" data-screen="capture">' +
            '<p class="pyc-eyebrow">Present Your Cards</p>' +
            '<h2 class="pyc-title" id="pycTitleCapture">Tell us what you have</h2>' +
            '<p class="pyc-sub">One slab or a full collection, we would love to see it. Share a few quick details and a few photos, and we will personally review it and follow up with a fair offer.</p>' +

            '<div class="pyc-field">' +
              '<label for="pycEmail">Where should we send your offer?</label>' +
              '<input type="email" id="pycEmail" autocomplete="email" inputmode="email" placeholder="you@email.com" required />' +
              '<p class="pyc-err" id="pycErrEmail">Please enter a valid email so we can send your offer.</p>' +
            '</div>' +
            '<div class="pyc-field">' +
              '<label for="pycName">Name <span class="opt">(optional)</span></label>' +
              '<input type="text" id="pycName" autocomplete="name" placeholder="First name is fine" />' +
            '</div>' +

            '<div class="pyc-more" id="pycMore">' +
              '<p class="pyc-more-head">A few details help <span class="pyc-more-head__opt">all optional, skip anything</span></p>' +
              '<div class="pyc-group">' +
                '<span class="pyc-label">What is in the collection?</span>' +
                '<div class="pyc-chips" data-pyc-group="type" role="group" aria-label="Collection type">' +
                  chip('Sports cards') + chip('Pokémon') + chip('One Piece') + chip('Disney') +
                  chip('Marvel') + chip('Star Wars') + chip('Graded slabs') + chip('Mixed') + chip('Not sure') +
                '</div>' +
              '</div>' +
              '<div class="pyc-group">' +
                '<span class="pyc-label">How did you hear about us?</span>' +
                '<div class="pyc-chips" data-pyc-group="heard" role="group" aria-label="How you heard about us">' +
                  chip('Repeat customer') + chip('Google') + chip('eBay') + chip('Instagram') +
                  chip('Facebook') + chip('TikTok') + chip('YouTube') + chip('Someone told me') +
                  chip('Somewhere else') +
                '</div>' +
              '</div>' +
              '<div class="pyc-group">' +
                '<span class="pyc-label">How would you like to take your offer?</span>' +
                '<p class="pyc-sub">Cash, or use Vault Credit to turn the cards you\'re done with into the cards you want.</p>' +
                '<div class="pyc-chips" data-pyc-group="payout" role="group" aria-label="Preferred payout">' +
                  chip('Cash') + chip('Vault Credit') + chip('Either / Not sure') +
                '</div>' +
              '</div>' +
              '<div class="pyc-group">' +
                '<label class="pyc-label" for="pycNote">Anything you want to add?</label>' +
                '<textarea id="pycNote" rows="3" placeholder="A sentence or two is plenty. Or leave it blank."></textarea>' +
              '</div>' +
              '<label class="pyc-checkrow"><input type="checkbox" id="pycDontKnow" /> <span>I do not know much about the collection. That is completely fine, just go by the photos.</span></label>' +
            '</div>' +
          '</section>' +

          /* SCREEN 2 — PHOTOS (encouraged, never required) */
          '<section class="pyc-screen" data-screen="photos">' +
            '<p class="pyc-eyebrow">A Little Visual</p>' +
            '<h2 class="pyc-title">Add a few photos</h2>' +
            '<p class="pyc-sub">Photos help us review faster and make you a stronger offer. The most helpful shots are the front and back of your standout cards and any slabs. For larger lots, a few overview photos are perfect. Add what you can now, or send more by email later.</p>' +
            '<div class="pyc-drop">' +
              '<div class="pyc-drop__icon"><svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="12" cy="12" r="3.2"/><path d="M8 5l1.2-2h5.6L16 5"/></svg></div>' +
              '<p class="pyc-drop__hint">Front and back is ideal, especially for graded slabs and key cards. Straight from your phone is perfect, no need to know players, sets, or values.</p>' +
              '<div class="pyc-pickrow">' +
                '<button class="btn btn--gold" type="button" data-pyc-pick="library">Add Photos</button>' +
                '<button class="btn btn--ghost" type="button" data-pyc-pick="camera">Take a Photo</button>' +
              '</div>' +
              '<input type="file" id="pycFileLib" accept="image/*" multiple />' +
              '<input type="file" id="pycFileCam" accept="image/*" capture="environment" />' +
            '</div>' +
            '<div class="pyc-thumbs" id="pycThumbs" aria-live="polite"></div>' +
            '<p class="pyc-count" id="pycCount" hidden></p>' +
            '<div id="pycTurnstile" style="margin:16px 0 0; display:flex; justify-content:center;"></div>' +
            '<p class="pyc-err" id="pycErrSend"></p>' +
          '</section>' +

          /* SCREEN 3 — CONFIRMATION */
          '<section class="pyc-screen" data-screen="done">' +
            '<div class="pyc-done">' +
              '<span class="pyc-done__seal"><svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="m5 13 4 4L19 7"/></svg></span>' +
              '<h2 class="pyc-title">Thank you for presenting your collection.</h2>' +
              '<p class="pyc-sub">We will personally review it and reach out by email, usually within 24 hours. If it is a fit we will send a fair range to start, no obligation, with your exact offer confirmed once we verify the cards. If it is not, we will tell you honestly and point you in the right direction.</p>' +
              '<p class="pyc-sub" id="pycRef" hidden></p>' +
            '</div>' +
          '</section>' +

        '</div>' +

        '<div class="pyc-foot" id="pycFoot"></div>' +
      '</div>';

    document.body.appendChild(flow);

    els.modal = flow.querySelector('.pyc-modal');
    els.body = flow.querySelector('.pyc-body');
    els.foot = flow.querySelector('#pycFoot');
    els.stepNum = flow.querySelector('#pycStepNum');
    els.steps = flow.querySelectorAll('.pyc-step');
    els.thumbs = flow.querySelector('#pycThumbs');
    els.count = flow.querySelector('#pycCount');
    els.fileLib = flow.querySelector('#pycFileLib');
    els.fileCam = flow.querySelector('#pycFileCam');
    els.email = flow.querySelector('#pycEmail');

    wire();
  }

  function chip(label) {
    return '<button class="pyc-chip" type="button" aria-pressed="false">' + label + '</button>';
  }

  /* ---------------------------------------------------------------- wiring */
  function wire() {
    flow.addEventListener('click', function (e) {
      if (e.target.closest('[data-pyc-dismiss]')) { e.preventDefault(); closeFlow(); }
    });

    /* Continue stays live (Option B): clicking with an invalid email shows the
       inline error + focuses the field (see go()); clear that error once valid. */
    els.email.addEventListener('input', function () {
      if (validEmail(els.email.value.trim())) hideErr('pycErrEmail');
    });

    /* photo pickers */
    flow.querySelector('[data-pyc-pick="library"]').addEventListener('click', function () { els.fileLib.click(); });
    flow.querySelector('[data-pyc-pick="camera"]').addEventListener('click', function () { els.fileCam.click(); });
    els.fileLib.addEventListener('change', onFiles);
    els.fileCam.addEventListener('change', onFiles);

    /* chips */
    flow.querySelectorAll('[data-pyc-group]').forEach(function (group) {
      var multi = group.getAttribute('data-pyc-group') === 'type';
      group.addEventListener('click', function (e) {
        var c = e.target.closest('.pyc-chip'); if (!c) return;
        var on = c.getAttribute('aria-pressed') === 'true';
        if (multi) {
          c.setAttribute('aria-pressed', on ? 'false' : 'true');
        } else {
          group.querySelectorAll('.pyc-chip').forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
          c.setAttribute('aria-pressed', 'true');
        }
      });
    });

    /* focus trap */
    flow.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); closeFlow(); return; }
      if (e.key !== 'Tab') return;
      var f = focusable(); if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  function focusable() {
    return Array.prototype.filter.call(
      flow.querySelectorAll('button, a[href], input, textarea, [tabindex]:not([tabindex="-1"])'),
      function (el) {
        if (el.disabled || el.hidden) return false;
        if (el.closest('.pyc-more') && el.closest('.pyc-more').hidden) return false;
        if (el.closest('.pyc-screen') && !el.closest('.pyc-screen.is-active')) return false;
        return el.offsetParent !== null || el === document.activeElement;
      }
    );
  }

  /* ---------------------------------------------------------------- photos */
  function onFiles(e) {
    var files = e.target.files; if (!files) return;
    Array.prototype.forEach.call(files, function (f) {
      if (f && /^image\//.test(f.type)) {
        var p = { file: f, url: URL.createObjectURL(f), name: f.name || 'photo.jpg' };
        state.photos.push(p);
        enqueueUpload(p);          // starts uploading immediately, in the background
      }
    });
    e.target.value = '';
    if (state.photos.length && !state.sentPhotoAddedEvent) {
      state.sentPhotoAddedEvent = true;      /* first photo only, once per lead_ref */
      track('seller_photo_added', { lead_ref: state.leadRef });
    }
    renderThumbs();
  }

  function renderThumbs() {
    els.thumbs.innerHTML = state.photos.map(function (p, i) {
      var st = '';
      if (p.status === 'uploading') {
        st = '<span class="pyc-thumb__st pyc-thumb__st--up" role="img" aria-label="Photo ' + (i + 1) + ' uploading"></span>';
      } else if (p.status === 'done') {
        st = '<span class="pyc-thumb__st pyc-thumb__st--ok" role="img" aria-label="Photo ' + (i + 1) + ' uploaded">' +
          '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" aria-hidden="true"><path d="m5 13 4 4L19 7"/></svg></span>';
      } else if (p.status === 'failed') {
        st = '<button class="pyc-thumb__st pyc-thumb__st--err" type="button" data-retry="' + i + '" aria-label="Photo ' + (i + 1) + ' failed to upload. Tap to retry">' +
          '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" aria-hidden="true"><path d="M3 12a9 9 0 1 1 3 6.7"/><path d="M3 20v-5h5"/></svg></button>';
      }
      return '<div class="pyc-thumb' + (p._shown ? ' pyc-thumb--settled' : '') + '">' +
        '<img src="' + p.url + '" alt="Collection photo ' + (i + 1) + '" />' +
        '<button class="pyc-thumb__rm" type="button" data-rm="' + i + '" aria-label="Remove photo ' + (i + 1) + '">' +
          '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
        '</button>' + st + '</div>';
    }).join('');
    state.photos.forEach(function (p) { p._shown = true; });   /* pop-in animates once, not on every re-render */
    els.thumbs.querySelectorAll('[data-rm]').forEach(function (b) {
      b.addEventListener('click', function () { removePhoto(parseInt(b.getAttribute('data-rm'), 10)); });
    });
    els.thumbs.querySelectorAll('[data-retry]').forEach(function (b) {
      b.addEventListener('click', function () {
        var p = state.photos[parseInt(b.getAttribute('data-retry'), 10)];
        if (p) { enqueueUpload(p); renderThumbs(); }
      });
    });
    var n = state.photos.length;
    var failed = 0, uploading = 0, done = 0;
    state.photos.forEach(function (p) {
      if (p.status === 'failed') failed++;
      else if (p.status === 'uploading') uploading++;
      else if (p.status === 'done') done++;
    });
    els.count.hidden = n === 0;
    els.count.classList.toggle('is-warn', failed > 0);
    els.count.innerHTML = !n ? '' :
      failed ? '<b>' + failed + '</b> photo' + (failed === 1 ? '' : 's') + ' did not upload. Tap the badge on the photo to retry, or continue without ' + (failed === 1 ? 'it' : 'them') + '.' :
      uploading ? '<b>' + n + '</b> photo' + (n === 1 ? '' : 's') + ' added &middot; uploading&hellip;' :
      '<b>' + n + '</b> photo' + (n === 1 ? '' : 's') + ' added' + (done === n && n > 0 ? ' &middot; all uploaded' : '');
    if (state.screen === 'photos') renderFoot();   // refresh the forward-button label
  }

  function removePhoto(i) {
    var p = state.photos[i];
    if (p) {
      p.removed = true;              // an in-flight upload result is discarded
      if (p.url) { try { URL.revokeObjectURL(p.url); } catch (e) {} }
    }
    state.photos.splice(i, 1);
    renderThumbs();
  }

  /* ---------------------------------------------------------------- state read */
  function readAll() {
    state.name = val('#pycName');
    state.email = val('#pycEmail');
    state.note = val('#pycNote');
    state.dontknow = !!(flow.querySelector('#pycDontKnow') || {}).checked;
    var typeGroup = flow.querySelector('[data-pyc-group="type"]');
    state.type = Array.prototype.filter.call(typeGroup.querySelectorAll('.pyc-chip'), function (c) {
      return c.getAttribute('aria-pressed') === 'true';
    }).map(function (c) { return c.textContent.trim(); });
    var heardSel = flow.querySelector('[data-pyc-group="heard"] .pyc-chip[aria-pressed="true"]');
    state.heard = heardSel ? heardSel.textContent.trim() : '';
    var payoutSel = flow.querySelector('[data-pyc-group="payout"] .pyc-chip[aria-pressed="true"]');
    state.payout = payoutSel ? payoutSel.textContent.trim() : '';
  }
  function val(sel) { var el = flow.querySelector(sel); return el ? el.value.trim() : ''; }

  /* ---------------------------------------------------------------- screens */
  function showScreen(name) {
    state.screen = name;
    flow.querySelectorAll('.pyc-screen').forEach(function (s) {
      s.classList.toggle('is-active', s.getAttribute('data-screen') === name);
    });
    els.body.scrollTop = 0;
    updateStepper(name);
    renderFoot();
    if (name === 'photos') {
      renderTurnstile();
      /* once per lead_ref: reaching the photos step again on the same journey
         (back and forward, reopen) is not a new funnel stage */
      if (!state.sentPhotosEvent) {
        state.sentPhotosEvent = true;
        track('seller_step_photos', { lead_ref: state.leadRef });
      }
    }
    var h = flow.querySelector('.pyc-screen.is-active .pyc-title');
    if (h) { h.setAttribute('tabindex', '-1'); try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); } }
  }

  function updateStepper(name) {
    var step = STEP_OF[name] || 1;
    els.steps.forEach(function (el, idx) {
      var n = idx + 1;
      el.classList.toggle('is-done', n < step || name === 'done');
      el.classList.toggle('is-active', n === step && name !== 'done');
    });
    els.stepNum.textContent = name === 'done' ? 'Complete' : ('Step ' + step + ' of 3');
  }

  function renderFoot() {
    var f = els.foot;
    if (state.screen === 'capture') {
      f.innerHTML =
        '<div class="pyc-foot__spacer"></div>' +
        '<button class="btn btn--gold" type="button" data-go="photos">Continue</button>';
      f.querySelector('[data-go="photos"]').addEventListener('click', function () { go('photos'); });
    } else if (state.screen === 'photos') {
      var label = state.photos.length ? 'Continue' : 'Continue Without Photos';
      f.innerHTML =
        '<button class="pyc-link" type="button" data-go="capture">Back</button>' +
        '<div class="pyc-foot__spacer"></div>' +
        '<button class="btn btn--gold" type="button" data-submit>' + label + '<span class="pyc-spin" aria-hidden="true"></span></button>';
      f.querySelector('[data-go="capture"]').addEventListener('click', function () { go('capture'); });
      f.querySelector('[data-submit]').addEventListener('click', submit);
    } else { /* done */
      f.innerHTML =
        '<div class="pyc-foot__spacer"></div>' +
        '<button class="btn btn--gold" type="button" data-done>Close</button>';
      f.querySelector('[data-done]').addEventListener('click', function () { state.submitted = true; closeFlow(); });
    }
  }

  function go(target) {
    if (state.screen === 'capture' && target === 'photos') {
      readAll();
      if (!validEmail(state.email)) { showErr('pycErrEmail'); if (els.email) els.email.focus(); return; }
    }
    showScreen(target);
  }

  /* ---------------------------------------------------------------- submit */
  function submit() {
    /* PHASE 1: in-flight guard. The button also disables below, but a ghost
       double-click (two pointer events ~1ms apart, seen live on 7/25) can land
       before the DOM write. This flag is set synchronously, so the second
       click is a no-op instead of a second Basin row. */
    if (state.inFlight) return;
    readAll();
    if (!validEmail(state.email)) { showScreen('capture'); showErr('pycErrEmail'); return; }
    state.inFlight = true;
    hideErr('pycErrSend');
    var btn = els.foot.querySelector('[data-submit]');
    if (btn) { btn.classList.add('btn--busy'); btn.disabled = true; btn.innerHTML = 'Sending<span class="pyc-spin" aria-hidden="true"></span>'; }
    var hadToken = !!turnstileToken();
    track('seller_submit_attempt', { lead_ref: state.leadRef, photos: state.photos.length,
                                     turnstile: hadToken ? 'ok' : tsState });
    /* photos are already uploading since they were added; wait only for
       stragglers. Then make sure the Turnstile token is FRESH (a token minted
       when the photos screen opened dies in ~5 min; an expired one used to
       submit empty and arrive spam-flagged as failed_turnstile with no
       notification). Success-gated: confirmation only on a real HTTP 200. */
    Promise.all([waitForUploads(), freshTurnstileToken()]).then(function (r) {
      var up = r[0];
      state.photoUrls = up.urls; state.photoFailed = up.failed;
      state.tsToken = r[1];
      return deliver(SUBJECT);
    }).then(function () {
      state.inFlight = false;
      track('seller_submit_ok', { lead_ref: state.leadRef, photos: state.photoUrls.length,
                                  first_touch_source: (window.MBMForms && window.MBMForms.firstTouchSource) ? window.MBMForms.firstTouchSource() : '',
                                  heard: state.heard || '(not specified)' });
      /* acquisition_lead = the GA4 Key Event. Fires ONLY here: after Basin
         accepted with HTTP 200. Never on CTA, open, start, or attempt. */
      track('acquisition_lead', { lead_ref: state.leadRef });
      finishSubmit(btn);
    }).catch(function (err) {
      state.inFlight = false;
      track('seller_submit_fail', { lead_ref: state.leadRef,
                                    reason: (err && err.message) ? String(err.message).slice(0, 40) : 'unknown' });
      renderFoot();        // restore the submit button + its label
      showSendError();
    });
  }

  /* ---- photo uploads: upload-on-select with per-photo status -------------
     Each photo starts uploading the MOMENT it is added: normalized to a JPEG
     processing master (at most 4032 px on the long edge, see shrink), uploaded max
     3 at a time, retried twice. The thumbnail shows a live status: spinner
     while uploading, a gold check when stored, a tappable retry badge on
     failure, so the seller can fix a failure BEFORE submitting. Submit then
     only waits for stragglers. A failed photo never blocks the lead.
     No-op when Cloudinary is not configured (photos listed by filename). */
  var UPLOAD_POOL = 3, upActive = 0, upQueue = [];

  /* ZERO-TOUCH-CAPTURE-1 (2026-09-14): the PROCESSING-MASTER envelope.
     Every photo is normalized on the seller's device to a JPEG whose longest
     edge is at most CAPTURE_MAX_EDGE, never upscaled. 4032 px is the ordinary
     12 MP phone frame and is the smallest cap at which the Vault's 600 px
     evidence floor still clears a 9-pocket binder page (931 px per card),
     six slabs (822 px) or four cards across (965 px); the old 2000 px cap
     cleared none of those (462 / 408 / 479 px). Measured through the Vault's
     own crop cut, see the release evidence. Two ceilings bound it from above:
     legacy iOS canvases stop at 16.7 MP (4032x3024 = 12.2 MP) and the
     Cloudinary image cap is 10 MB (4032 at q0.85 is ~2-3.5 MB).

     The canvas output is ALWAYS what uploads (never "the original when it is
     smaller"): the re-encode is what guarantees a decodable JPEG, a baked-in
     orientation and NO camera EXIF / GPS in the seller's upload, and at 4032
     an ordinary 12 MP photo is exactly at the cap, so a size-based preference
     would have decided by a coin flip whether the camera metadata travelled.
     A photo the browser cannot decode at all (some HEIC on non-Apple
     browsers) is refused honestly rather than uploaded raw: the server cannot
     read HEIC either and would silently see a zero-size image. The lead is
     never blocked by a photo. */
  var CAPTURE_MAX_EDGE = 4032;
  var CAPTURE_JPEG_Q = 0.85;

  /* first-bytes sniff, mirroring the server: JPEG / PNG / WEBP only. Trusting
     file.type is not enough: pictures arriving from a share sheet are often
     mislabelled. Resolves true/false, never throws. */
  function sniffDecodable(file) {
    return new Promise(function (resolve) {
      try {
        var r = new FileReader();
        r.onload = function () {
          try {
            var b = new Uint8Array(r.result || []);
            var jpeg = b.length > 2 && b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF;
            var png = b.length > 7 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47;
            var webp = b.length > 11 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 &&
                       b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50;
            resolve(!!(jpeg || png || webp));
          } catch (e) { resolve(false); }
        };
        r.onerror = function () { resolve(false); };
        r.readAsArrayBuffer(file.slice(0, 12));
      } catch (e) { resolve(false); }
    });
  }

  /* -> Blob (the normalized JPEG) or null (undecodable: refused, never raw).
     The original file is a fallback ONLY when it is positively a decodable
     JPEG/PNG/WEBP and the canvas path itself failed. */
  function shrink(file) {
    return new Promise(function (resolve) {
      if (!file) { resolve(null); return; }
      var url = URL.createObjectURL(file);
      var img = new Image();
      var settled = false;
      function done(v) { if (settled) return; settled = true; try { URL.revokeObjectURL(url); } catch (e) {} resolve(v); }
      function fallback() {
        sniffDecodable(file).then(function (ok) { done(ok ? file : null); });
      }
      img.onload = function () {
        try {
          var w = img.naturalWidth, h = img.naturalHeight;
          if (!w || !h) { fallback(); return; }
          var s = Math.min(1, CAPTURE_MAX_EDGE / Math.max(w, h));
          var c = document.createElement('canvas');
          c.width = Math.max(1, Math.round(w * s)); c.height = Math.max(1, Math.round(h * s));
          c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
          c.toBlob(function (blob) {
            if (!blob || !blob.size) { fallback(); return; }
            done(blob);                      /* canvas output is ALWAYS authoritative */
          }, 'image/jpeg', CAPTURE_JPEG_Q);
        } catch (e) { fallback(); }
      };
      img.onerror = function () { fallback(); };
      img.src = url;
    });
  }

  /* one upload, 45s cap (a real abort, not just a race, so a stuck upload frees
     the uplink + its pool slot), retried `tries` more times with backoff (1.2s
     then 2.4s) before resolving null. */
  function put(blob, tries) {
    var endpoint = 'https://api.cloudinary.com/v1_1/' + CLOUDINARY_CLOUD + '/upload';
    var fd = new FormData();
    fd.append('file', blob);
    fd.append('upload_preset', CLOUDINARY_PRESET);
    var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, 45000);
    return fetch(endpoint, { method: 'POST', body: fd, signal: ctrl ? ctrl.signal : undefined })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (j) { clearTimeout(timer); return j.secure_url || j.url || null; })
      .catch(function () {
        clearTimeout(timer);
        if (tries <= 0) return null;
        var delay = (3 - tries) * 1200;             /* 1.2s before retry 1, 2.4s before retry 2 */
        return new Promise(function (res) { setTimeout(res, delay > 0 ? delay : 1200); })
          .then(function () { return put(blob, tries - 1); });
      });
  }

  function enqueueUpload(p) {
    if (!CLOUDINARY_CLOUD || !CLOUDINARY_PRESET) return;   // unconfigured: filename-only path
    p.status = 'uploading'; p.secure = null;
    upQueue.push(p);
    pumpUploads();
  }

  function pumpUploads() {
    while (upActive < UPLOAD_POOL && upQueue.length) {
      var p = upQueue.shift();
      if (!p.removed) startUpload(p);
    }
  }

  function startUpload(p) {
    upActive++;
    shrink(p.file).then(function (b) { return b ? put(b, 2) : null; }).then(function (url) {
      upActive--;
      if (!p.removed) {
        p.status = url ? 'done' : 'failed';
        p.secure = url || null;
        renderThumbs();
      }
      if (p.waiters) { p.waiters.forEach(function (w) { w(); }); p.waiters = null; }
      pumpUploads();
    });
  }

  /* submit-time: wait only for photos still in flight, then report results. */
  function waitForUploads() {
    var pending = state.photos.filter(function (p) { return p.status === 'uploading'; });
    return Promise.all(pending.map(function (p) {
      return new Promise(function (res) { (p.waiters = p.waiters || []).push(res); });
    })).then(function () {
      var urls = state.photos.map(function (p) { return p.secure; }).filter(Boolean);
      var failed = state.photos.filter(function (p) { return p.status === 'failed'; }).length;
      return { urls: urls, failed: failed };
    });
  }

  function finishSubmit(btn) {
    if (btn) { btn.classList.remove('btn--busy'); btn.disabled = false; }
    /* PHASE 1 fix (found in staging): mark the journey complete HERE, not only
       on the done-screen Close button. A seller who dismisses with the X (or a
       test that reopens straight from a CTA) used to reopen into the OLD state,
       which after Phase 1 would have reused the OLD lead_ref for a second
       submission and broken the 1:1 lead_ref <-> Basin-row join. */
    state.submitted = true;
    /* the seller-visible copy of lead_ref: quote it in any email and we can
       find the exact Basin submission */
    var ref = flow.querySelector('#pycRef');
    if (ref && refCode()) {
      ref.textContent = 'Your reference: ' + refCode() + '. Keep it handy if you email us about this submission.';
      ref.hidden = false;
    }
    showScreen('done');
  }

  function payload() {
    var data = {
      Email: state.email,
      Name: state.name || '(not given)',
      'Collection type': state.type.length ? state.type.join(', ') : '(not specified)',
      'How they heard about us': state.heard || '(not specified)',
      'Preferred payout': state.payout || '(not specified)',
      'Knows the collection': state.dontknow ? 'No, asked us to go by the photos' : 'Not stated',
      Note: state.note || '(none)'
    };
    if (state.photoUrls && state.photoUrls.length) {
      data.Photos = state.photoUrls.length + ' photo(s) uploaded' + (state.photoFailed ? ' (' + state.photoFailed + ' failed to upload)' : '') + ':';
      state.photoUrls.forEach(function (u, i) { data['Photo ' + (i + 1)] = u; });
    } else if (state.photos.length) {
      data.Photos = state.photos.length + ' photo(s) selected (' + state.photos.map(function (p) { return p.name; }).join(', ') + '). ' +
        (state.photoFailed ? 'Upload failed; ' : 'Files not attached; ') + 'request them in your reply (reply-to is the seller).';
    } else {
      data.Photos = 'None selected. Invite the seller to reply with photos.';
    }
    data.Source = 'AURUM Get an Offer';
    /* deterministic arrival data (utm / click id / referrer, first-touch, captured
       site-wide on landing). The seller's own "How did you hear about us?" answer
       covers word of mouth and dark social that no tag can see; this covers the
       clicks that answer decays on. The two are SEPARATE facts and stay separate
       fields: never merge or reconcile them. Guarded so it can never block a lead. */
    try {
      var attr = (window.MBMForms && window.MBMForms.attribution) ? window.MBMForms.attribution() : null;
      if (attr) { Object.keys(attr).forEach(function (k) { data[k] = attr[k]; }); }
    } catch (e) {}
    /* PHASE 1: join key + submission context. "Lead ref" is the random id the
       seller also sees as their reference code; "CTA" is which button opened
       the flow; "Submitted from" is the page the flow ran on (path only). */
    if (state.leadRef) { data['Lead ref'] = state.leadRef; data['Reference code'] = refCode(); }
    if (state.cta) data['CTA'] = state.cta;
    try { data['Submitted from'] = window.location.pathname; } catch (e) {}
    var tok = state.tsToken != null ? state.tsToken : turnstileToken();
    data['cf-turnstile-response'] = tok;
    if (!tok) data['Turnstile'] = 'no token (' + (tsState === 'unloaded' ? 'blocked or not loaded' : tsState) + ')';
    return data;
  }

  function deliver(subject) {
    var data = payload();
    // Single shared backend adapter. kind:'acquisition' selects the acquisition
    // endpoint + the Vault-Credit-aware confirmation copy (one source of truth).
    if (window.MBMForms && typeof window.MBMForms.send === 'function') {
      return window.MBMForms.send(subject, data, { replyTo: state.email, kind: 'acquisition' });
    }
    return Promise.reject(new Error('no-router'));
  }

  /* honest failure: keep the seller on the photos step with a prefilled mailto so
     the lead is never lost (matches the project's success-only-on-200 rule) */
  function showSendError() {
    var e = flow.querySelector('#pycErrSend');
    if (!e) return;
    var mailto = (window.MBMForms && window.MBMForms.mailtoFor)
      ? window.MBMForms.mailtoFor(SUBJECT, payload())
      : 'mailto:mintbymemphis@gmail.com?subject=' + encodeURIComponent(SUBJECT);
    e.innerHTML = 'We could not send that just now. Please email us at <a href="' + mailto + '">mintbymemphis@gmail.com</a> and we will take care of you right away.';
    e.classList.add('show');
  }

  function validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }
  function showErr(id) { var e = flow.querySelector('#' + id); if (e) e.classList.add('show'); }
  function hideErr(id) { var e = flow.querySelector('#' + id); if (e) e.classList.remove('show'); }

  /* ---------------------------------------------------------------- open/close */
  function openFlow(trigger) {
    if (!flow) build();
    loadTurnstile();
    lastTrigger = trigger || document.activeElement;
    if (state.submitted) hardReset();
    /* lead_ref: mint once per flow session; remember which CTA opened it.
       Reopening without submitting keeps both (same journey, same reference). */
    if (!state.leadRef) state.leadRef = mintLeadRef();
    if (trigger && trigger.getAttribute && trigger.getAttribute('data-cta')) {
      state.cta = trigger.getAttribute('data-cta');
    }
    document.body.classList.add('pyc-lock');
    flow.setAttribute('aria-hidden', 'false');
    flow.classList.add('on');
    renderThumbs();
    showScreen('capture');
    track('seller_flow_open', { cta: state.cta || '(none)', lead_ref: state.leadRef,
                                page_path: window.location.pathname });
  }

  function closeFlow() {
    if (!flow) return;
    flow.classList.remove('on');
    flow.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('pyc-lock');
    if (lastTrigger && lastTrigger.focus) { try { lastTrigger.focus(); } catch (e) {} }
  }

  function hardReset() {
    state.photos.forEach(function (p) { p.removed = true; if (p.url) { try { URL.revokeObjectURL(p.url); } catch (e) {} } });
    state = resetState();
    flow.querySelectorAll('.pyc-chip').forEach(function (c) { c.setAttribute('aria-pressed', 'false'); });
    ['#pycName', '#pycEmail', '#pycNote'].forEach(function (s) { var el = flow.querySelector(s); if (el) el.value = ''; });
    var dk = flow.querySelector('#pycDontKnow'); if (dk) dk.checked = false;
    if (window.turnstile && tsWidgetId !== null) { try { window.turnstile.reset(tsWidgetId); } catch (e) {} }
    hideErr('pycErrEmail'); hideErr('pycErrSend');
  }

  /* delegated opener for every Get-an-Offer / Present CTA */
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-present]'); if (!t) return;
    e.preventDefault();
    openFlow(t);
  });

  window.MBMPresent = { open: openFlow, close: closeFlow };
})();

/* lock the page behind the flow */
(function () {
  var s = document.createElement('style');
  s.textContent = 'body.pyc-lock{overflow:hidden!important;}';
  document.head.appendChild(s);
})();
